import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { MemoryProposalItemView, MemoryProposedAction, ProposedActionStatus } from '../chat/types';

type MemoryProposalResponse = {
  id: string;
  reason: string;
  status: ProposedActionStatus;
  failureReason: string | null;
  items: MemoryProposalItemView[];
};

export function toMemoryProposedAction(proposal: MemoryProposalResponse): MemoryProposedAction {
  return {
    kind: 'MEMORY',
    proposalId: proposal.id,
    reason: proposal.reason,
    items: proposal.items,
    status: proposal.status,
    failureReason: proposal.failureReason,
  };
}

export function usePendingMemoryProposals(conversationId: string | undefined, enabled: boolean) {
  const { data, isError } = useQuery<MemoryProposalResponse[]>({
    queryKey: ['conversations', conversationId, 'memory-proposals', { status: 'PENDING' }],
    enabled: enabled && !!conversationId,
    // Flag-gating means a 404 during rollout; never retry or surface it as an error state.
    retry: false,
  });
  return { pendingProposals: isError ? [] : (data ?? []) };
}

export function useMemoryProposalDecision(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { data: MemoryProposalResponse },
    Error,
    { proposalId: string; decision: 'APPROVE' | 'DENY' }
  >({
    mutationFn: ({ proposalId, decision }) =>
      api.post<MemoryProposalResponse>(
        `conversations/${conversationId}/memory-proposals/${proposalId}/decision`,
        { decision },
      ),
    onSuccess: response => {
      if (response.data.status === 'APPLIED') {
        // The approved batch changed stored memories server-side; the memories pages and
        // sections all key their queries under ['memories', ...].
        queryClient.invalidateQueries({ queryKey: ['memories'] });
      }
    },
    // Invalidate on failure too: a 409/404 means the proposal was decided elsewhere, and the
    // pending list must refresh so an orphan-recovered widget disappears rather than staying
    // clickable.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'memory-proposals'],
      });
    },
  });
  return { decideMemoryProposal: mutateAsync, isDecidingMemory: isPending };
}
